import { CropIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ImageCropGuide } from '../../../../rapid-ui'
import type { Background } from '../../data/v1'
import { useDesignerActions } from '../../use-designer-actions'
import { QuickToolButton, QuickToolPopover } from './primitives'

function getImageBackgroundSrc(background: Background, externalBackgroundUrl: string): string | null {
	if (background.mode === 'image' && background.imageUrl) { return background.imageUrl }
	if (background.mode === 'externalImage' && externalBackgroundUrl) { return externalBackgroundUrl }
	return null
}

export function ImageCropQuickTool(
	{ layerId, active, onToggle, background, externalBackgroundUrl, layerEl }: {
		layerId: string
		active: boolean
		onToggle: () => void
		background: Background
		externalBackgroundUrl: string
		layerEl: HTMLElement | null
	},
) {
	const { updateLayer } = useDesignerActions()
	const btnRef = useRef<HTMLButtonElement>(null)

	const imageSrc = getImageBackgroundSrc(background, externalBackgroundUrl)

	const [layerSize, setLayerSize] = useState({ w: 0, h: 0 })
	useEffect(() => {
		if (!layerEl) {
			setLayerSize({ w: 0, h: 0 })
			return
		}
		const observer = new ResizeObserver(([entry]) => {
			setLayerSize({ w: entry.contentRect.width, h: entry.contentRect.height })
		})
		observer.observe(layerEl)
		return () => observer.disconnect()
	}, [layerEl])

	const handleOffsetChange = useCallback((imageOffsetX: number, imageOffsetY: number, transient?: boolean) => {
		updateLayer(layerId, (prev) => {
			if (!('background' in prev)) { return {} }
			return { background: { ...prev.background, imageOffsetX, imageOffsetY } }
		}, transient ? { transient: true } : undefined)
	}, [layerId, updateLayer])

	const handleScaleChange = useCallback((imageScale: number, transient?: boolean) => {
		updateLayer(layerId, (prev) => {
			if (!('background' in prev)) { return {} }
			return { background: { ...prev.background, imageScale } }
		}, transient ? { transient: true } : undefined)
	}, [layerId, updateLayer])

	if (!imageSrc) { return null }

	return (
		<>
			<QuickToolButton
				buttonRef={btnRef}
				active={active}
				onClick={onToggle}
				title="Image crop"
			>
				<CropIcon size={14} />
			</QuickToolButton>

			<QuickToolPopover
				anchorEl={btnRef.current}
				open={active}
				onClose={onToggle}
			>
				<div className="p-2">
					<ImageCropGuide
						src={imageSrc}
						imageScale={background.imageScale}
						imageOffsetX={background.imageOffsetX}
						imageOffsetY={background.imageOffsetY}
						containerWidth={layerSize.w}
						containerHeight={layerSize.h}
						enableGlobalKeyboardNudge
						onOffsetChange={handleOffsetChange}
						onScaleChange={handleScaleChange}
					/>
				</div>
			</QuickToolPopover>
		</>
	)
}
